function pixelateSubtleEncryptedReveal(selector, {
  duration = 8.5,          
  initialPixelSize = 3,    
  midpointClarity = 300,   
  bounceBackSize = 220     
} = {}) {
  const img = document.querySelector(selector);
  if (!img || !img.complete) {
    img?.addEventListener("load", () => pixelateSubtleEncryptedReveal(selector, { duration, initialPixelSize, midpointClarity, bounceBackSize }), { once: true });
    return;
  }

  const container = img.parentElement;
  if (getComputedStyle(container).position === "static") {
    container.style.position = "relative";
  }

  const rect = img.getBoundingClientRect();
  const dispW = Math.round(rect.width);
  const dispH = Math.round(rect.height);
  const natW = img.naturalWidth;
  const natH = img.naturalHeight;

  const style = getComputedStyle(img);
  const fit = style.objectFit || "fill";
  const [xRaw, yRaw] = (style.objectPosition || "50% 50%").split(" ");
  const xVal = parseFloat(xRaw) / 100 || 0.5;
  const yVal = parseFloat(yRaw) / 100 || 0.5;

  let uMin = 0, vMin = 0, uMax = 1, vMax = 1;
  if (fit === "cover" || fit === "contain") {
    const scale = fit === "cover"
      ? Math.max(dispW / natW, dispH / natH)
      : Math.min(dispW / natW, dispH / natH);
    const drawW = natW * scale;
    const drawH = natH * scale;
    const extraW = Math.max(0, drawW - dispW);
    const extraH = Math.max(0, drawH - dispH);

    uMin = (extraW / drawW) * xVal;
    uMax = 1 - (extraW / drawW) * (1 - xVal);
    vMin = (extraH / drawH) * (1 - yVal);
    vMax = 1 - (extraH / drawH) * yVal;
  }

  const canvas = document.createElement("canvas");
  canvas.width = dispW;
  canvas.height = dispH;
  Object.assign(canvas.style, {
    position: "absolute",
    top: "0",
    left: "0",
    width: `${dispW}px`,
    height: `${dispH}px`,
    pointerEvents: "none",
    zIndex: "10"
  });
  img.insertAdjacentElement("afterend", canvas);

  const gl = canvas.getContext("webgl");
  if (!gl) {
    img.style.opacity = "1";
    canvas.remove();
    return;
  }

  gl.viewport(0, 0, canvas.width, canvas.height);

  const vsSource = `
    attribute vec2 aPos;
    varying vec2 vUV;
    void main() {
      vUV = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0, 1);
    }
  `;

  // Flush-left #ifdefs are required for strict mobile WebGL parsers
  const fsSource = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform sampler2D uImage;
uniform float uPixelX;
uniform float uPixelY;
uniform float uSaturation;
uniform float uDecryption;
uniform vec4 uCrop;
varying vec2 vUV;

float rand(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec2 safeUV = clamp(vUV, 0.0, 0.999);

  vec2 blockUV = vec2(
    floor(safeUV.x * uPixelX) / uPixelX,
    floor(safeUV.y * uPixelY) / uPixelY
  );
  
  vec2 blockId = vec2(floor(safeUV.x * uPixelX), floor(safeUV.y * uPixelY));
  
  vec2 scrambleOffset = vec2(
    rand(blockId) * 2.0 - 1.0,
    rand(blockId + vec2(100.0, 100.0)) * 2.0 - 1.0
  ) * uDecryption;
  
  vec2 finalUV = fract(blockUV + scrambleOffset);
  
  vec2 cropped = mix(uCrop.xy, uCrop.zw, finalUV);
  vec4 color = texture2D(uImage, cropped);
  
  float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
  vec3 grayscale = vec3(luminance);
  
  color.rgb = mix(grayscale, color.rgb, uSaturation);
  
  gl_FragColor = color;
}
  `;

  function createShader(src, type) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s));
    }
    return s;
  }

  const vert = createShader(vsSource, gl.VERTEX_SHADER);
  const frag = createShader(fsSource, gl.FRAGMENT_SHADER);
  const program = gl.createProgram();
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.useProgram(program);

  const posLoc = gl.getAttribLocation(program, "aPos");
  const posBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  1, 1,
    -1, -1,  1,  1, -1, 1
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  
  // The fatal error line has been eradicated. Properly prefixed wrap parameter:
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  
  gl.uniform1i(gl.getUniformLocation(program, "uImage"), 0);

  const uPixelX = gl.getUniformLocation(program, "uPixelX");
  const uPixelY = gl.getUniformLocation(program, "uPixelY");
  const uSaturation = gl.getUniformLocation(program, "uSaturation");
  const uDecryption = gl.getUniformLocation(program, "uDecryption");
  const uCrop = gl.getUniformLocation(program, "uCrop");

  gl.uniform4f(uCrop, uMin, vMin, uMax, vMax);

  const state = { 
    blocks: initialPixelSize,
    saturation: 0.0,
    decrypt: 0.3
  };

  function render() {
    gl.uniform1f(uPixelX, state.blocks);
    gl.uniform1f(uPixelY, state.blocks * (dispH / dispW));
    gl.uniform1f(uSaturation, state.saturation);
    gl.uniform1f(uDecryption, state.decrypt);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  img.style.opacity = "0";
  render();

  gsap.to(state, {
    keyframes: [
      { blocks: midpointClarity, saturation: 0.85, decrypt: 0.0, duration: duration * 0.5, ease: "expo.inOut" },
      { blocks: bounceBackSize, saturation: 0.75, decrypt: 0.005, duration: duration * 0.15, ease: "sine.inOut" },
      { blocks: dispW, saturation: 1.0, decrypt: 0.0, duration: duration * 0.35, ease: "sine.inOut" }
    ],
    onUpdate: render,
  });

  gsap.to(img.style, {
    opacity: "1",
    duration: duration * 0.1,
    delay: duration * 0.9,
    ease: "linear",
    onComplete() {
      requestAnimationFrame(() => canvas.remove());
    }
  });
}

// Usage
pixelateSubtleEncryptedReveal("#owl_img", {
  duration: 7,           
  initialPixelSize: 3,     
  midpointClarity: 300,    
  bounceBackSize: 220      
});